// src/screens/ProfileScreen.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged, updateProfile } from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  where,
  getCountFromServer,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

import {
  User,
  Building2,
  Tags,
  Camera,
  Phone,
  Mail,
  Home,
  MapPinned,
  Users,
  Facebook,
  Linkedin,
  Instagram,
  Globe,
  Moon,
  Sun,
  Loader2,
} from "lucide-react";
import {
  getSampleOrderStatusPreview,
  normalizeCompanyType,
} from "../utils/sampleOrderStatusLabels";

/**
 * ✅ IMPORTANT FIX:
 * These UI components MUST NOT be declared inside ProfileScreen,
 * otherwise they get re-created every render and inputs lose focus.
 */
const Field = React.memo(function Field({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  icon,
}) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold text-slate-600 mb-1">{label}</div>
      <div className="relative">
        {icon && (
          <div className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400">
            {icon}
          </div>
        )}
        <input
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder || ""}
          disabled={disabled}
          className={[
            "w-full h-9 rounded-lg border text-[13px] outline-none",
            icon ? "pl-8 pr-2.5" : "px-2.5",
            disabled
              ? "bg-slate-50 border-slate-200 text-slate-600"
              : "bg-white border-slate-200 text-slate-900 focus:border-slate-400",
          ].join(" ")}
        />
      </div>
    </label>
  );
});

const SocialRow = React.memo(function SocialRow({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  icon,
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-32 flex items-center gap-1 text-[11px] font-semibold text-slate-600">
        <span className="text-slate-400">{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <input
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder || ""}
        disabled={disabled}
        className={[
          "flex-1 h-9 rounded-lg border text-[13px] outline-none px-2.5",
          disabled
            ? "bg-slate-50 border-slate-200 text-slate-600"
            : "bg-white border-slate-200 text-slate-900 focus:border-slate-400",
        ].join(" ")}
      />
    </div>
  );
});


const InlineField = React.memo(function InlineField({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  labelClassName = "w-14",
  className = "",
  inputClassName = "",
}) {
  return (
    <div className={`flex items-center gap-2 min-w-0 ${className}`.trim()}>
      <div className={`${labelClassName} shrink-0 text-[11px] font-semibold text-slate-600`}>{label}</div>
      <input
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder || ""}
        disabled={disabled}
        className={[
          "flex-1 min-w-0 h-9 rounded-lg border text-[13px] outline-none px-2.5",
          disabled
            ? "bg-slate-50 border-slate-200 text-slate-600"
            : "bg-white border-slate-200 text-slate-900 focus:border-slate-400",
          inputClassName,
        ].join(" ")}
      />
    </div>
  );
});

const Chip = React.memo(function Chip({ children, tone = "default" }) {
  return (
    <span
      className={[
        "inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium",
        tone === "primary"
          ? "border-sky-200 bg-sky-50 text-sky-800"
          : "border-slate-200 bg-white text-slate-700",
      ].join(" ")}
    >
      {children}
    </span>
  );
});

const Stat = React.memo(function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
        {label}
      </div>
      <div className="text-[14px] font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
});

const ProfileScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Firebase
  const auth = getAuth();
  const db = getFirestore();
  const storage = getStorage();

  // -----------------------------
  // ✅ BASE PATH FIX (/app support)
  // -----------------------------
  const rawPath = (location.pathname || "").split("?")[0].split("#")[0];
  const APP_BASE = rawPath.startsWith("/app") ? "/app" : "";
  const buildAppPath = (p) => {
    const cleaned = p.startsWith("/") ? p : `/${p}`;
    return APP_BASE ? `${APP_BASE}${cleaned}` : cleaned;
  };

  // Core state
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  const [companyId, setCompanyId] = useState(null);
  const [company, setCompany] = useState(null);

  const [loading, setLoading] = useState(true);

  // Profile picture
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const fileInputRef = useRef(null);

  // ✅ Company logo
  const [uploadingCompanyLogo, setUploadingCompanyLogo] = useState(false);
  const [companyLogoPreview, setCompanyLogoPreview] = useState("");
  const companyLogoInputRef = useRef(null);

  // Tabs
  const [activeTab, setActiveTab] = useState("profile"); // "profile" | "company"

  // Edit modes
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingCompany, setEditingCompany] = useState(false);

  // Save flags
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [applyingCompanyWording, setApplyingCompanyWording] = useState(false);
  const [themeMode, setThemeMode] = useState(() => localStorage.getItem("supplyhawk-theme") || "light");
  const [savingTheme, setSavingTheme] = useState(false);

  // Groups + Territories (assigned only)
  const [myGroups, setMyGroups] = useState([]);
  const [assignedTerritories, setAssignedTerritories] = useState([]);

  // Counts
  const [sampleAssignedCount, setSampleAssignedCount] = useState(0);
  const [accountsAssignedToMeCount, setAccountsAssignedToMeCount] = useState(0);
  const [accountsInMyTerritoriesCount, setAccountsInMyTerritoriesCount] = useState(0);
  const [accountsOverlapCount, setAccountsOverlapCount] = useState(0);

  // Forms
  const [profileForm, setProfileForm] = useState({
    fullName: "",
    phone: "",
    officePhone: "",
    mailingStreet: "",
    mailingCity: "",
    mailingState: "",
    mailingZip: "",
    facebook: "",
    linkedin: "",
    instagram: "",
    website: "",
  });

  const [companyForm, setCompanyForm] = useState({
    companyName: "",
    companyEmail: "",
    companyPhone: "",
    addressStreet: "",
    addressCity: "",
    addressState: "",
    addressZip: "",
    companyType: "",
    useIndustrySampleStatusLabels: false,
  });

  const role = (userProfile?.role || "").toString().toLowerCase();
  const isAdminUser = role === "admin" || role === "owner";

  // ------------------------------------
  // Helpers
  // ------------------------------------
  const chunk = (arr, size = 10) => {
    const a = Array.isArray(arr) ? arr : [];
    const out = [];
    for (let i = 0; i < a.length; i += size) out.push(a.slice(i, i + size));
    return out;
  };

  const safeStr = (v) => (v == null ? "" : String(v));

  const getTerritoryIdsFromUser = (u) => {
    const ids = [];
    if (Array.isArray(u?.territoryIds)) ids.push(...u.territoryIds.filter(Boolean));
    if (u?.territoryId) ids.push(u.territoryId);
    return Array.from(new Set(ids.map(String)));
  };

  const getCountSafe = async (qObj) => {
    try {
      const snap = await getCountFromServer(qObj);
      const count = snap?.data?.()?.count;
      return typeof count === "number" ? count : 0;
    } catch (err) {
      try {
        const snap = await getDocs(qObj);
        return snap.size || 0;
      } catch (e2) {
        return 0;
      }
    }
  };

  const openTeam = () => navigate(buildAppPath("/team"));

  // ------------------------------------
  // Load user + company + assigned territories + groups + counts
  // ------------------------------------
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setLoading(true);

      if (!u) {
        setFirebaseUser(null);
        setUserProfile(null);
        setCompany(null);
        setCompanyId(null);
        setMyGroups([]);
        setAssignedTerritories([]);
        setSampleAssignedCount(0);
        setAccountsAssignedToMeCount(0);
        setAccountsInMyTerritoriesCount(0);
        setAccountsOverlapCount(0);
        setCompanyLogoPreview("");
        setLoading(false);
        return;
      }

      setFirebaseUser(u);

      try {
        const userRef = doc(db, "users", u.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          setUserProfile(null);
          setCompany(null);
          setCompanyId(null);
          setMyGroups([]);
          setAssignedTerritories([]);
          setSampleAssignedCount(0);
          setAccountsAssignedToMeCount(0);
          setAccountsInMyTerritoriesCount(0);
          setAccountsOverlapCount(0);
          setCompanyLogoPreview("");
          setLoading(false);
          return;
        }

        const userData = userSnap.data() || {};
        setUserProfile(userData);
        setThemeMode((userData?.preferredTheme || localStorage.getItem("supplyhawk-theme") || "light").toString().toLowerCase() === "dark" ? "dark" : "light");

        // photo preview
        const p = userData.photoURL || u.photoURL || "";
        setPhotoPreview(p);

        // forms
        const mailing = userData?.mailingAddress || {};
        const socials = userData?.socialLinks || {};

        setProfileForm({
          fullName: userData.fullName || u.displayName || "",
          phone: userData.phone || "",
          officePhone: userData.officePhone || "",
          mailingStreet: mailing.street || "",
          mailingCity: mailing.city || "",
          mailingState: mailing.state || "",
          mailingZip: mailing.zip || "",
          facebook: socials.facebook || "",
          linkedin: socials.linkedin || "",
          instagram: socials.instagram || "",
          website: socials.website || "",
        });

        const compId = userData.companyId || u.uid;
        setCompanyId(compId);

        // Company
        await reloadCompany(compId, userData);

        // Groups
        await loadGroups(compId, userData);

        // Assigned territories only
        const myTerritoryIds = getTerritoryIdsFromUser(userData);
        await loadAssignedTerritories(compId, myTerritoryIds);

        // Counts
        await loadCounts({
          compId,
          uid: u.uid,
          myTerritoryIds,
        });
      } catch (err) {
        console.error("[ProfileScreen] load error:", err);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadCompany = async (compId, userDataFallback) => {
    try {
      const companyRef = doc(db, "companies", compId);
      const snap = await getDoc(companyRef);

      if (snap.exists()) {
        const c = snap.data() || {};
        setCompany(c);

        const logo = c.logoUrl || c.logoURL || c.logo || "";
        setCompanyLogoPreview(logo || "");

        setCompanyForm({
          companyName: c.businessName || c.name || "",
          companyEmail: c.companyEmail || "",
          companyPhone: c.companyPhone || "",
          addressStreet: c.address?.street || "",
          addressCity: c.address?.city || "",
          addressState: c.address?.state || "",
          addressZip: c.address?.zip || "",
          companyType: c.businessType || c.companyType || "",
          useIndustrySampleStatusLabels: c.useIndustrySampleStatusLabels === true,
        });
      } else {
        const u = userDataFallback || {};
        const fallback = {
          businessName: u.businessName || u.companyName || u.fullName || "Your Company",
          companyEmail: u.email || "",
          companyPhone: u.phone || "",
          address: { street: "", city: "", state: "", zip: "" },
          logoUrl: "",
        };
        setCompany(fallback);
        setCompanyLogoPreview("");

        setCompanyForm({
          companyName: fallback.businessName || "",
          companyEmail: fallback.companyEmail || "",
          companyPhone: fallback.companyPhone || "",
          addressStreet: "",
          addressCity: "",
          addressState: "",
          addressZip: "",
          companyType: "",
          useIndustrySampleStatusLabels: false,
        });
      }
    } catch (err) {
      console.error("[ProfileScreen] reloadCompany error:", err);
    }
  };

  const loadGroups = async (compId, userData) => {
    try {
      const groupIds = Array.isArray(userData?.groupIds) ? userData.groupIds : [];
      if (!compId || groupIds.length === 0) {
        setMyGroups([]);
        return;
      }

      const reads = await Promise.all(
        groupIds.map(async (gid) => {
          try {
            const gRef = doc(db, "companies", compId, "groups", gid);
            const gSnap = await getDoc(gRef);
            return gSnap.exists() ? { id: gSnap.id, ...(gSnap.data() || {}) } : null;
          } catch {
            return null;
          }
        })
      );

      setMyGroups(reads.filter(Boolean));
    } catch (err) {
      console.error("[ProfileScreen] loadGroups error:", err);
      setMyGroups([]);
    }
  };

  const loadAssignedTerritories = async (compId, territoryIds) => {
    try {
      const ids = Array.isArray(territoryIds) ? territoryIds : [];
      if (!compId || ids.length === 0) {
        setAssignedTerritories([]);
        return;
      }

      const reads = await Promise.all(
        ids.map(async (tid) => {
          try {
            const tRef = doc(db, "companies", compId, "territories", tid);
            const tSnap = await getDoc(tRef);
            return tSnap.exists() ? { id: tSnap.id, ...(tSnap.data() || {}) } : null;
          } catch {
            return null;
          }
        })
      );

      const cleaned = reads.filter(Boolean);
      cleaned.sort((a, b) => safeStr(a.name).localeCompare(safeStr(b.name)));
      setAssignedTerritories(cleaned);
    } catch (err) {
      console.error("[ProfileScreen] loadAssignedTerritories error:", err);
      setAssignedTerritories([]);
    }
  };

  const loadCounts = async ({ compId, uid, myTerritoryIds }) => {
    try {
      setSampleAssignedCount(0);
      setAccountsAssignedToMeCount(0);
      setAccountsInMyTerritoriesCount(0);
      setAccountsOverlapCount(0);

      if (!compId || !uid) return;

      const samplesCountFromCollection = async (collName) => {
        try {
          const base = collection(db, "companies", compId, collName);
          const q1 = query(base, where("assignedToId", "==", uid));
          return await getCountSafe(q1);
        } catch {
          return 0;
        }
      };

      const so = await samplesCountFromCollection("sampleOrders");
      const sr = so === 0 ? await samplesCountFromCollection("sampleRequests") : 0;
      setSampleAssignedCount(so + sr);

      const accountsCol = collection(db, "companies", compId, "accounts");
      const qAssignedToMe = query(accountsCol, where("assignedToId", "==", uid));
      const assignedToMeCount = await getCountSafe(qAssignedToMe);
      setAccountsAssignedToMeCount(assignedToMeCount);

      const tIds = Array.isArray(myTerritoryIds) ? myTerritoryIds.filter(Boolean) : [];
      let territoryCountTotal = 0;
      let overlapTotal = 0;

      if (tIds.length > 0) {
        const chunks = chunk(tIds, 10);

        for (const ch of chunks) {
          const qTerr = query(accountsCol, where("territoryId", "in", ch));
          territoryCountTotal += await getCountSafe(qTerr);

          const qOverlap = query(
            accountsCol,
            where("assignedToId", "==", uid),
            where("territoryId", "in", ch)
          );
          overlapTotal += await getCountSafe(qOverlap);
        }
      }

      setAccountsInMyTerritoriesCount(territoryCountTotal);
      setAccountsOverlapCount(overlapTotal);
    } catch (err) {
      console.error("[ProfileScreen] loadCounts error:", err);
    }
  };

  // ------------------------------------
  // Photo upload (profile)
  // ------------------------------------
  const triggerFileInput = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !auth.currentUser) return;

    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      alert("Please upload a valid image file (JPEG, PNG, GIF, or WebP).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be less than 5MB.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result || "");
    reader.readAsDataURL(file);

    try {
      setUploadingPhoto(true);

      const uid = auth.currentUser.uid;
      const ext = file.name.split(".").pop() || "jpg";
      const fileName = `profile_${uid}_${Date.now()}.${ext}`;
      const storageRef = ref(storage, `profile-pictures/${fileName}`);

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await updateProfile(auth.currentUser, { photoURL: url });
      await updateDoc(doc(db, "users", uid), {
        photoURL: url,
        updatedAt: serverTimestamp(),
      });

      setPhotoPreview(url);
      setUserProfile((prev) => ({ ...(prev || {}), photoURL: url }));
    } catch (err) {
      console.error("[ProfileScreen] Photo upload failed:", err);
      alert("Could not upload photo.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ------------------------------------
  // ✅ Company logo upload (admin/owner only)
  // ------------------------------------
  const triggerCompanyLogoInput = () => companyLogoInputRef.current?.click();

  const handleCompanyLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !auth.currentUser) return;

    if (!isAdminUser) {
      alert("Only an Admin/Owner can update the company logo.");
      return;
    }
    if (!companyId) {
      alert("Missing companyId.");
      return;
    }

    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      alert("Please upload a valid image file (JPEG, PNG, GIF, WebP, or SVG).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be less than 5MB.");
      return;
    }

    // preview immediately
    if (file.type !== "image/svg+xml") {
      const reader = new FileReader();
      reader.onloadend = () => setCompanyLogoPreview(reader.result || "");
      reader.readAsDataURL(file);
    } else {
      const objUrl = URL.createObjectURL(file);
      setCompanyLogoPreview(objUrl);
    }

    try {
      setUploadingCompanyLogo(true);

      const extFromName = (file.name.split(".").pop() || "").toLowerCase();
      const ext = extFromName || (file.type === "image/svg+xml" ? "svg" : "png");
      const fileName = `logo_${Date.now()}.${ext}`;
      const storageRef = ref(storage, `company-logos/${companyId}/${fileName}`);

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const companyRef = doc(db, "companies", companyId);

      try {
        await updateDoc(companyRef, {
          logoUrl: url,
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        await setDoc(
          companyRef,
          {
            companyId,
            logoUrl: url,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      setCompanyLogoPreview(url);
      setCompany((prev) => ({ ...(prev || {}), logoUrl: url }));
    } catch (err) {
      console.error("[ProfileScreen] Company logo upload failed:", err);
      alert("Could not upload company logo.");
    } finally {
      setUploadingCompanyLogo(false);
    }
  };

  // ------------------------------------
  // Save profile (endUser allowed)
  // ------------------------------------
  const handleSaveProfile = async () => {
    if (!auth.currentUser) return;
    setSavingProfile(true);

    try {
      const uid = auth.currentUser.uid;

      const payload = {
        fullName: (profileForm.fullName || "").trim(),
        phone: (profileForm.phone || "").trim(),
        officePhone: (profileForm.officePhone || "").trim(),
        mailingAddress: {
          street: (profileForm.mailingStreet || "").trim(),
          city: (profileForm.mailingCity || "").trim(),
          state: (profileForm.mailingState || "").trim(),
          zip: (profileForm.mailingZip || "").trim(),
        },
        socialLinks: {
          facebook: (profileForm.facebook || "").trim(),
          linkedin: (profileForm.linkedin || "").trim(),
          instagram: (profileForm.instagram || "").trim(),
          website: (profileForm.website || "").trim(),
        },
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "users", uid), payload);

      try {
        await updateProfile(auth.currentUser, { displayName: payload.fullName });
      } catch (_) {}

      setUserProfile((prev) => ({ ...(prev || {}), ...payload }));
      setEditingProfile(false);
    } catch (err) {
      console.error("[ProfileScreen] Save profile failed:", err);
      alert(err?.message || "Could not save profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  // ------------------------------------
  // Save company (admin/owner only)
  // ------------------------------------
  const handleSaveCompany = async () => {
    if (!auth.currentUser) return;

    if (!isAdminUser) {
      setEditingCompany(false);
      return;
    }

    if (!companyId) {
      alert("Missing companyId.");
      return;
    }

    setSavingCompany(true);

    try {
      const uid = auth.currentUser.uid;
      const nextCompanyType =
        normalizeCompanyType(companyForm.companyType) ||
        (companyForm.companyType || "").trim() ||
        null;
      const currentCompanyType =
        normalizeCompanyType(companyDisplay.companyType) ||
        (companyDisplay.companyType || "").trim() ||
        null;
      const wordingSettingChanged =
        currentCompanyType !== nextCompanyType ||
        (companyDisplay.useIndustrySampleStatusLabels === true) !==
          (companyForm.useIndustrySampleStatusLabels === true);

      if (wordingSettingChanged) {
        setApplyingCompanyWording(true);
      }

      const payload = {
        companyId,
        ownerUid: company?.ownerUid || uid,
        businessName: (companyForm.companyName || "").trim(),
        companyEmail: (companyForm.companyEmail || "").trim(),
        companyPhone: (companyForm.companyPhone || "").trim(),
        companyType: nextCompanyType,
        businessType: nextCompanyType,
        useIndustrySampleStatusLabels: companyForm.useIndustrySampleStatusLabels === true,
        address: {
          street: (companyForm.addressStreet || "").trim(),
          city: (companyForm.addressCity || "").trim(),
          state: (companyForm.addressState || "").trim(),
          zip: (companyForm.addressZip || "").trim(),
        },
        updatedAt: serverTimestamp(),
      };

      const companyRef = doc(db, "companies", companyId);
      try {
        await updateDoc(companyRef, payload);
      } catch (err) {
        await setDoc(companyRef, payload, { merge: true });
      }

      if (wordingSettingChanged) {
        await new Promise((resolve) => window.setTimeout(resolve, 1200));
      }

      setCompany((prev) => ({
        ...(prev || {}),
        ...payload,
      }));

      setEditingCompany(false);
    } catch (err) {
      console.error("[ProfileScreen] Save company failed:", err);
      alert(err?.message || "Could not save company.");
    } finally {
      setApplyingCompanyWording(false);
      setSavingCompany(false);
    }
  };


  const handleThemeChange = async (nextTheme) => {
    if (!auth.currentUser) return;
    const normalizedTheme = nextTheme === "dark" ? "dark" : "light";
    setThemeMode(normalizedTheme);
    localStorage.setItem("supplyhawk-theme", normalizedTheme);
    document.documentElement.setAttribute("data-theme", normalizedTheme);
    document.body.setAttribute("data-theme", normalizedTheme);
    window.dispatchEvent(new CustomEvent("supplyhawk-theme-change", { detail: { theme: normalizedTheme } }));

    try {
      setSavingTheme(true);
      await updateDoc(doc(db, "users", auth.currentUser.uid), {
        preferredTheme: normalizedTheme,
        updatedAt: serverTimestamp(),
      });
      setUserProfile((prev) => ({ ...(prev || {}), preferredTheme: normalizedTheme }));
    } catch (err) {
      console.error("[ProfileScreen] Save theme failed:", err);
      alert(err?.message || "Could not save theme preference.");
    } finally {
      setSavingTheme(false);
    }
  };

  // ------------------------------------
  // Derived company display (read-only)
  // ------------------------------------
  const companyDisplay = useMemo(() => {
    const c = company || {};
    const addr = c.address || {};
    return {
      name: c.businessName || c.name || "Company",
      email: c.companyEmail || "",
      phone: c.companyPhone || "",
      street: addr.street || "",
      city: addr.city || "",
      state: addr.state || "",
      zip: addr.zip || "",
      logoUrl: c.logoUrl || c.logoURL || c.logo || "",
      companyType: c.businessType || c.companyType || "",
      useIndustrySampleStatusLabels: c.useIndustrySampleStatusLabels === true,
    };
  }, [company]);

  const previewCompanyType = editingCompany ? companyForm.companyType : companyDisplay.companyType;
  const previewNormalizedCompanyType = useMemo(
    () => normalizeCompanyType(previewCompanyType),
    [previewCompanyType]
  );
  const previewUseIndustrySampleStatusLabels = editingCompany
    ? companyForm.useIndustrySampleStatusLabels === true
    : companyDisplay.useIndustrySampleStatusLabels === true;

  const sampleStatusLabelPreview = useMemo(
    () =>
      getSampleOrderStatusPreview({
        companyType: previewCompanyType,
        businessType: previewCompanyType,
        useIndustrySampleStatusLabels: previewUseIndustrySampleStatusLabels,
      }),
    [
      previewCompanyType,
      previewUseIndustrySampleStatusLabels,
    ]
  );

  // Keep preview synced if companyDisplay.logoUrl changes
  useEffect(() => {
    if (companyDisplay.logoUrl && !companyLogoPreview) {
      setCompanyLogoPreview(companyDisplay.logoUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyDisplay.logoUrl]);

  // ------------------------------------
  // Render
  // ------------------------------------
  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-sm text-slate-600">Loading…</div>
      </div>
    );
  }

  if (!firebaseUser) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-sm text-slate-600">No user logged in.</div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none mx-0 px-0 py-1">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* ✅ NOTHING above the tabs */}
        <div className="border-b border-slate-200 px-4">
          <div className="flex items-center justify-between gap-3">
            {/* Tabs */}
            <nav className="flex items-center gap-2 py-2 text-sm">
              <button
                type="button"
                onClick={() => setActiveTab("profile")}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-medium transition-all ${
                  activeTab === "profile"
                    ? "text-blue-600 bg-blue-50"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                <User className="w-4 h-4" />
                <span>Profile</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("company")}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-medium transition-all ${
                  activeTab === "company"
                    ? "text-blue-600 bg-blue-50"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                <Building2 className="w-4 h-4" />
                <span>Company</span>
              </button>
            </nav>

          </div>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          {/* PROFILE TAB */}
          {activeTab === "profile" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {/* Profile Card */}
              <section className="rounded-xl border border-slate-200 p-3 lg:col-span-2">
                <div className="flex items-start justify-between mb-2 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="relative shrink-0">
                      <div className="w-16 h-16 rounded-2xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center">
                        {photoPreview ? (
                          <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-6 h-6 text-slate-500" />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={triggerFileInput}
                        disabled={uploadingPhoto}
                        className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center hover:bg-slate-50 disabled:opacity-60"
                        title="Update photo"
                      >
                        {uploadingPhoto ? (
                          <div className="w-4 h-4 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" />
                        ) : (
                          <Camera className="w-4 h-4 text-slate-600" />
                        )}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleFileChange}
                      />
                    </div>

                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold tracking-tight text-slate-900 flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-600" />
                        Profile
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">Keep your info up to date.</div>
                    </div>
                  </div>

                  {!editingProfile ? (
                    <button
                      type="button"
                      onClick={() => setEditingProfile(true)}
                      className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Edit
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const u = userProfile || {};
                          const mailing = u.mailingAddress || {};
                          const socials = u.socialLinks || {};
                          setProfileForm({
                            fullName: u.fullName || firebaseUser.displayName || "",
                            phone: u.phone || "",
                            officePhone: u.officePhone || "",
                            mailingStreet: mailing.street || "",
                            mailingCity: mailing.city || "",
                            mailingState: mailing.state || "",
                            mailingZip: mailing.zip || "",
                            facebook: socials.facebook || "",
                            linkedin: socials.linkedin || "",
                            instagram: socials.instagram || "",
                            website: socials.website || "",
                          });
                          setEditingProfile(false);
                        }}
                        className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveProfile}
                        disabled={savingProfile}
                        className="h-8 px-3 rounded-lg bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-800 disabled:opacity-60"
                      >
                        {savingProfile ? "Saving…" : "Save"}
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Field
                    label="Full Name"
                    value={profileForm.fullName}
                    onChange={(v) => setProfileForm((p) => ({ ...p, fullName: v }))}
                    disabled={!editingProfile}
                    icon={<User className="w-4 h-4" />}
                  />
                  <Field label="Email" value={firebaseUser.email || ""} disabled icon={<Mail className="w-4 h-4" />} />
                </div>

                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <InlineField
                    label="Mobile:"
                    value={profileForm.phone}
                    onChange={(v) => setProfileForm((p) => ({ ...p, phone: v }))}
                    disabled={!editingProfile}
                  />
                  <InlineField
                    label="Office:"
                    value={profileForm.officePhone}
                    onChange={(v) => setProfileForm((p) => ({ ...p, officePhone: v }))}
                    disabled={!editingProfile}
                  />
                </div>

                <div className="mt-3">
                  <div className="text-[12px] font-semibold text-slate-900 flex items-center gap-2 mb-2">
                    <Home className="w-4 h-4 text-slate-600" />
                    Mailing Address (Editable)
                  </div>

                  <div className="grid grid-cols-1 gap-2">
                    <InlineField
                      label="Street:"
                      value={profileForm.mailingStreet}
                      onChange={(v) => setProfileForm((p) => ({ ...p, mailingStreet: v }))}
                      disabled={!editingProfile}
                      labelClassName="w-12"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,220px)_auto_auto] gap-2 items-start">
                      <InlineField
                        label="City:"
                        value={profileForm.mailingCity}
                        onChange={(v) => setProfileForm((p) => ({ ...p, mailingCity: v }))}
                        disabled={!editingProfile}
                        labelClassName="w-10"
                        className="md:max-w-[220px]"
                      />
                      <InlineField
                        label="State:"
                        value={profileForm.mailingState}
                        onChange={(v) => setProfileForm((p) => ({ ...p, mailingState: v }))}
                        disabled={!editingProfile}
                        className="md:w-[132px]"
                        inputClassName="md:w-[64px] flex-none"
                      />
                      <InlineField
                        label="ZIP:"
                        value={profileForm.mailingZip}
                        onChange={(v) => setProfileForm((p) => ({ ...p, mailingZip: v }))}
                        disabled={!editingProfile}
                        className="md:w-[134px]"
                        inputClassName="md:w-[78px] flex-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Company Address (read-only) */}
                <div className="mt-3">
                  <div className="text-[12px] font-semibold text-slate-900 flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-slate-600" />
                    Company Address (Read-only)
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[13px] text-slate-700">
                    {[
                      companyDisplay.street,
                      [companyDisplay.city, companyDisplay.state].filter(Boolean).join(", "),
                      companyDisplay.zip,
                    ]
                      .filter(Boolean)
                      .join(" ")
                      .trim() || "No company address on file."}
                  </div>
                </div>

                {/* Social Links */}
                <div className="mt-3">
                  <div className="text-[12px] font-semibold text-slate-900 flex items-center gap-2 mb-2">
                    <Globe className="w-4 h-4 text-slate-600" />
                    Social Links
                  </div>

                  <div className="space-y-2">
                    <SocialRow
                      label="Facebook"
                      value={profileForm.facebook}
                      onChange={(v) => setProfileForm((p) => ({ ...p, facebook: v }))}
                      disabled={!editingProfile}
                      placeholder="https://facebook.com/..."
                      icon={<Facebook className="w-4 h-4" />}
                    />
                    <SocialRow
                      label="LinkedIn"
                      value={profileForm.linkedin}
                      onChange={(v) => setProfileForm((p) => ({ ...p, linkedin: v }))}
                      disabled={!editingProfile}
                      placeholder="https://linkedin.com/in/..."
                      icon={<Linkedin className="w-4 h-4" />}
                    />
                    <SocialRow
                      label="Instagram"
                      value={profileForm.instagram}
                      onChange={(v) => setProfileForm((p) => ({ ...p, instagram: v }))}
                      disabled={!editingProfile}
                      placeholder="https://instagram.com/..."
                      icon={<Instagram className="w-4 h-4" />}
                    />
                    <SocialRow
                      label="Website"
                      value={profileForm.website}
                      onChange={(v) => setProfileForm((p) => ({ ...p, website: v }))}
                      disabled={!editingProfile}
                      placeholder="https://..."
                      icon={<Globe className="w-4 h-4" />}
                    />
                  </div>
                </div>
              </section>

              {/* Access Card */}
              <section className="rounded-xl border border-slate-200 p-3 lg:col-span-1">
                <div className="text-[13px] font-semibold text-slate-900 flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-slate-600" />
                  Access & Workload
                </div>

                <div className="grid grid-cols-2 gap-2 mb-3">
                  <Stat label="Samples assigned to me" value={sampleAssignedCount} />
                  <Stat label="Accounts assigned to me" value={accountsAssignedToMeCount} />
                  <Stat label="Accounts in my territory" value={accountsInMyTerritoriesCount} />
                  <Stat label="Overlap (me + territory)" value={accountsOverlapCount} />
                </div>

                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[12px] font-semibold text-slate-900">Appearance</div>
                      <div className="text-[11px] text-slate-500">Choose the app theme for your account. Light is the default.</div>
                    </div>
                    {savingTheme ? <div className="text-[11px] text-slate-500">Saving…</div> : null}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => handleThemeChange("light")}
                      disabled={savingTheme || themeMode === "light"}
                      className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-[12px] font-semibold transition-colors ${
                        themeMode === "light"
                          ? "border-blue-200 bg-blue-50 text-blue-700"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <Sun className="w-4 h-4" />
                      <span>Light</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleThemeChange("dark")}
                      disabled={savingTheme || themeMode === "dark"}
                      className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-[12px] font-semibold transition-colors ${
                        themeMode === "dark"
                          ? "border-blue-200 bg-blue-50 text-blue-700"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <Moon className="w-4 h-4" />
                      <span>Dark blue</span>
                    </button>
                  </div>
                </div>


                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[12px] font-semibold text-slate-900 flex items-center gap-2">
                      <MapPinned className="w-4 h-4 text-slate-600" />
                      My Territories
                    </div>
                    <div className="text-[11px] text-slate-500">{assignedTerritories.length}</div>
                  </div>

                  {assignedTerritories.length ? (
                    <div className="space-y-2">
                      {assignedTerritories.map((t) => {
                        const cities = Array.isArray(t.cities) ? t.cities : [];
                        const states = Array.isArray(t.states) ? t.states : [];
                        const zipCodes = Array.isArray(t.zipCodes) ? t.zipCodes : [];
                        const vis = (t.visibility || "all").toString().toLowerCase();

                        return (
                          <div key={t.id} className="rounded-lg border border-slate-200 px-2.5 py-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-[12px] font-semibold text-slate-900 truncate">
                                {t.name || t.id}
                              </div>
                              <Chip tone="primary">{vis}</Chip>
                            </div>

                            <div className="mt-1 text-[11px] text-slate-600 flex flex-wrap gap-2">
                              {states.length > 0 && <span>States: {states.length}</span>}
                              {cities.length > 0 && <span>Cities: {cities.length}</span>}
                              {zipCodes.length > 0 && <span>Zips: {zipCodes.length}</span>}
                            </div>

                            {cities.length > 0 && (
                              <div className="mt-1 text-[11px] text-slate-500 truncate">
                                {cities.slice(0, 4).join(", ")}
                                {cities.length > 4 ? ` +${cities.length - 4}` : ""}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-[12px] text-slate-500">No territories assigned.</div>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[12px] font-semibold text-slate-900 flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-600" />
                      My Groups
                    </div>
                    <div className="text-[11px] text-slate-500">{myGroups.length}</div>
                  </div>

                  {myGroups.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {myGroups.map((g) => (
                        <Chip key={g.id}>{g.name || g.title || g.id}</Chip>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[12px] text-slate-500">No groups assigned.</div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="flex justify-end mb-2">
                    <div className="text-[11px] text-slate-500">
                      Company access:{" "}
                      <span className="font-semibold text-slate-700">
                        {isAdminUser ? "Admin / Owner" : "Read-only"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[12px] font-semibold text-slate-800">Team</div>
                    <button
                      type="button"
                      onClick={openTeam}
                      className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Open
                    </button>
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* COMPANY TAB */}
          {activeTab === "company" && (
            <div className="relative grid grid-cols-1 gap-4 lg:grid-cols-3">
              {applyingCompanyWording ? (
                <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-white/90 backdrop-blur-sm">
                  <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-xl">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                    <div className="mt-3 text-sm font-semibold text-slate-900">
                      Switching sample status wording
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      Applying your company type and wording preferences across sample order labels now.
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="lg:col-span-2 rounded-xl border border-slate-200 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[13px] font-semibold text-slate-900 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-600" />
                    Company
                  </div>

                  {isAdminUser ? (
                    !editingCompany ? (
                      <button
                        type="button"
                        onClick={() => setEditingCompany(true)}
                        className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Edit
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setCompanyForm({
                              companyName: companyDisplay.name || "",
                              companyEmail: companyDisplay.email || "",
                              companyPhone: companyDisplay.phone || "",
                              addressStreet: companyDisplay.street || "",
                              addressCity: companyDisplay.city || "",
                              addressState: companyDisplay.state || "",
                              addressZip: companyDisplay.zip || "",
                              companyType: companyDisplay.companyType || "",
                              useIndustrySampleStatusLabels: companyDisplay.useIndustrySampleStatusLabels === true,
                            });
                            setEditingCompany(false);
                          }}
                          className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveCompany}
                          disabled={savingCompany || applyingCompanyWording}
                          className="h-8 px-3 rounded-lg bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-800 disabled:opacity-60"
                        >
                          {applyingCompanyWording
                            ? "Applying…"
                            : savingCompany
                            ? "Saving…"
                            : "Apply"}
                        </button>
                      </div>
                    )
                  ) : (
                    <div className="text-[12px] text-slate-500 font-semibold">Read-only</div>
                  )}
                </div>

                <div className="mb-3 pb-3 border-b border-slate-100">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center shrink-0">
                        {companyLogoPreview ? (
                          <img
                            src={companyLogoPreview}
                            alt="Company Logo"
                            className="w-full h-full object-contain bg-white"
                            onError={() => setCompanyLogoPreview("")}
                          />
                        ) : (
                          <Building2 className="w-4 h-4 text-slate-400" />
                        )}
                      </div>

                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold text-slate-800 leading-tight">Company Logo</div>
                        <div className="text-[11px] text-slate-500 leading-tight truncate">
                          Used in the sidebar
                        </div>
                      </div>
                    </div>

                    {isAdminUser ? (
                      <button
                        type="button"
                        onClick={triggerCompanyLogoInput}
                        disabled={uploadingCompanyLogo}
                        className="h-7 px-2.5 rounded-md border border-slate-200 bg-white text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      >
                        {uploadingCompanyLogo ? "Uploading…" : "Upload"}
                      </button>
                    ) : (
                      <div className="text-[11px] text-slate-500 font-semibold shrink-0">Read-only</div>
                    )}

                    <input
                      ref={companyLogoInputRef}
                      type="file"
                      className="hidden"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                      onChange={handleCompanyLogoChange}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Field
                    label="Company Name"
                    value={editingCompany ? companyForm.companyName : companyDisplay.name}
                    onChange={(v) => setCompanyForm((p) => ({ ...p, companyName: v }))}
                    disabled={!editingCompany || !isAdminUser}
                    icon={<Building2 className="w-4 h-4" />}
                  />
                  <Field
                    label="Company Email"
                    value={editingCompany ? companyForm.companyEmail : companyDisplay.email}
                    onChange={(v) => setCompanyForm((p) => ({ ...p, companyEmail: v }))}
                    disabled={!editingCompany || !isAdminUser}
                    icon={<Mail className="w-4 h-4" />}
                  />
                  <Field
                    label="Company Phone"
                    value={editingCompany ? companyForm.companyPhone : companyDisplay.phone}
                    onChange={(v) => setCompanyForm((p) => ({ ...p, companyPhone: v }))}
                    disabled={!editingCompany || !isAdminUser}
                    icon={<Phone className="w-4 h-4" />}
                  />
                  <Field label="Company ID" value={companyId || ""} disabled />
                </div>

                <div className="mt-3">
                  <div className="text-[12px] font-semibold text-slate-900 flex items-center gap-2 mb-2">
                    <Home className="w-4 h-4 text-slate-600" />
                    Company Address
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Field
                      label="Street"
                      value={editingCompany ? companyForm.addressStreet : companyDisplay.street}
                      onChange={(v) => setCompanyForm((p) => ({ ...p, addressStreet: v }))}
                      disabled={!editingCompany || !isAdminUser}
                    />
                    <Field
                      label="City"
                      value={editingCompany ? companyForm.addressCity : companyDisplay.city}
                      onChange={(v) => setCompanyForm((p) => ({ ...p, addressCity: v }))}
                      disabled={!editingCompany || !isAdminUser}
                    />
                    <Field
                      label="State"
                      value={editingCompany ? companyForm.addressState : companyDisplay.state}
                      onChange={(v) => setCompanyForm((p) => ({ ...p, addressState: v }))}
                      disabled={!editingCompany || !isAdminUser}
                    />
                    <Field
                      label="ZIP"
                      value={editingCompany ? companyForm.addressZip : companyDisplay.zip}
                      onChange={(v) => setCompanyForm((p) => ({ ...p, addressZip: v }))}
                      disabled={!editingCompany || !isAdminUser}
                    />
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                  <div className="text-[12px] font-semibold text-slate-800">Team & Invites</div>
                  <button
                    type="button"
                    onClick={openTeam}
                    className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Open Team
                  </button>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-900">
                    <Tags className="h-4 w-4 text-slate-600" />
                    Company Type
                  </div>

                  <div className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Selected During Setup
                    </div>
                    {editingCompany && isAdminUser ? (
                      <div className="mt-2">
                        <select
                          value={companyForm.companyType || ""}
                          onChange={(e) =>
                            setCompanyForm((prev) => ({ ...prev, companyType: e.target.value }))
                          }
                          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Not set</option>
                          <option value="seafood">Seafood Distributor</option>
                          <option value="meat">Meat Distributor</option>
                          <option value="dairy">Dairy Distributor</option>
                          <option value="produce">Produce Distributor</option>
                          <option value="baking">Baking Distributor</option>
                        </select>
                      </div>
                    ) : (
                      <div className="mt-1 text-sm font-semibold text-slate-900">
                        {companyDisplay.companyType || "Not set"}
                      </div>
                    )}
                    <div className="mt-1 text-[11px] text-slate-500">
                      {previewNormalizedCompanyType
                        ? "Industry wording is available for this company type."
                        : "Standard wording will be used until a supported company type is set."}
                    </div>
                  </div>

                  <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[12px] font-semibold text-slate-900">
                          Sample Status Wording
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-500">
                          Keep the workflow the same and only change the wording.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (!editingCompany || !isAdminUser || !previewNormalizedCompanyType) return;
                          setCompanyForm((prev) => ({
                            ...prev,
                            useIndustrySampleStatusLabels: !prev.useIndustrySampleStatusLabels,
                          }));
                        }}
                        disabled={!editingCompany || !isAdminUser || !previewNormalizedCompanyType}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                          (editingCompany
                            ? companyForm.useIndustrySampleStatusLabels
                            : companyDisplay.useIndustrySampleStatusLabels)
                            ? "bg-blue-600"
                            : "bg-slate-300"
                        } ${
                          !editingCompany || !isAdminUser || !previewNormalizedCompanyType
                            ? "cursor-not-allowed opacity-60"
                            : ""
                        }`}
                        aria-label="Use industry wording"
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                            (editingCompany
                              ? companyForm.useIndustrySampleStatusLabels
                              : companyDisplay.useIndustrySampleStatusLabels)
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-2">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-[11px] font-semibold text-slate-900">Use standard labels</div>
                        <div className="mt-0.5 text-[11px] text-slate-500">
                          Show generic wording like Pending and Follow Up.
                        </div>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-[11px] font-semibold text-slate-900">Use industry wording</div>
                        <div className="mt-0.5 text-[11px] text-slate-500">
                          Show company-type wording like Packed on Ice or Cut / Packed.
                        </div>
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Preview
                      </div>
                      <div className="mt-2 space-y-1.5">
                        {sampleStatusLabelPreview.map((entry) => (
                          <div
                            key={entry.status}
                            className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2"
                          >
                            <span className="text-[11px] font-semibold text-slate-500">
                              {entry.status}
                            </span>
                            <span className="text-[12px] font-semibold text-slate-900 text-right">
                              {entry.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
